import React from 'react';
import { Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TenantStatus, DeploymentStatus } from '../types';

interface StatusChipProps {
  status: TenantStatus | DeploymentStatus;
  variant?: 'filled' | 'outlined';
  size?: 'small' | 'medium';
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
    case 'completed':
      return { color: '#155724', backgroundColor: '#d4edda' };
    case 'creating':
    case 'in_progress':
    case 'pending':
      return { color: '#856404', backgroundColor: '#fff3cd' };
    case 'failed':
      return { color: '#721c24', backgroundColor: '#f8d7da' };
    case 'deleting':
      return { color: '#8b6914', backgroundColor: '#ffeaa7' };
    case 'deleted':
      return { color: '#495057', backgroundColor: '#e2e3e5' };
    default:
      return { color: '#495057', backgroundColor: '#e2e3e5' };
  }
};

const getStatusLabel = (status: string, t: any) => {
  switch (status) {
    case 'creating':
      return t('status.creating');
    case 'active':
      return t('status.active');
    case 'failed':
      return t('status.failed');
    case 'deleting':
      return t('status.deleting');
    case 'deleted':
      return t('status.deleted');
    case 'pending':
      return t('status.pending');
    case 'in_progress':
      return t('status.in_progress');
    case 'completed':
      return t('status.completed');
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

const StatusChip: React.FC<StatusChipProps> = ({ 
  status, 
  variant = 'filled',
  size = 'small' 
}) => {
  const { t } = useTranslation();
  const { color, backgroundColor } = getStatusColor(status);
  const label = getStatusLabel(status, t);

  return (
    <Chip
      label={label}
      variant={variant}
      size={size}
      sx={{
        ...(variant === 'filled' && {
          color,
          backgroundColor,
          fontWeight: 600,
          fontSize: '0.75rem',
        }),
        ...(variant === 'outlined' && {
          color,
          borderColor: color,
          fontWeight: 600,
          fontSize: '0.75rem',
        }),
      }}
    />
  );
};

export default StatusChip;